import {useRef} from "react"
import ReactDOM from "react-dom"
import styled from "styled-components"
import {defaultThemeColors, mediaSizes} from "ui/theme"
import {hideScrollBar} from "../scrollbarStyle/scrollbarStyle"
import {usePreventScroll} from "../../../hooks/usePreventScroll";
import {useVisualViewport} from "../../../hooks/useVisualViewport";


export const Modal = ({
                          handleClose,
                          show,
                          children,
                          minHeight = 300,
                          maxWidth = 700,
                          minWidth,
                          widthPercentage,
                          contentWrapperHeight = "auto",
                          setContentHeightFull,
                          isNoPaddingX,
                          bgColor,
                          closeBtnColor,
                          borderRadius,
                          fromTop,
                          // Phones: sheet uses the full visible height (booking, search).
                          mobileFull,
                      }) => {
    const node = useRef()
    const overlay = useRef()
    useVisualViewport(overlay, show)

    // usePreventScroll(show)

    return ReactDOM.createPortal(
        <ModalDiv ref={overlay} show={show} widthPercentage={widthPercentage}>
            <ContentWrapper
                ref={node}
                minHeight={minHeight}
                contentWrapperHeight={contentWrapperHeight}
                minWidth={minWidth}
                maxWidth={maxWidth}
                widthPercentage={widthPercentage}
                show={show}
                bgColor={bgColor}
                borderRadius={borderRadius}
                fromTop={fromTop}
                mobileFull={mobileFull}
            >
                <DragHandle/>
                <ContentDiv setContentHeightFull={setContentHeightFull} isNoPaddingX={isNoPaddingX}>
                    {children}
                    {handleClose && (
                        <ContentCloseBtn onClick={handleClose} closeBtnColor={closeBtnColor}>
                            <CloseIcon/>
                        </ContentCloseBtn>
                    )}
                </ContentDiv>
            </ContentWrapper>
        </ModalDiv>,
        document.getElementById("modal-root"),
    )
}

const CloseIcon = () => (
    <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <line x1="18" y1="6" x2="6" y2="18"></line>
        <line x1="6" y1="6" x2="18" y2="18"></line>
    </svg>
)

const ModalDiv = styled.div`
    visibility: ${({show}) => (show ? "visible" : "hidden")};
    opacity: ${({show}) => (show ? 1 : 0)};
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.6);

    /* Phones: follow the visible area (keyboard open or not). */
    @media (width < ${mediaSizes.md}) {
        top: var(--vv-top, 0);
        height: var(--vv-height, 100%);
    }
    z-index: 99;
    transition-property: all;
    transition-duration: 300ms;
`

const DragHandle = styled.div`
    width: 96px;
    height: 4px;
    background-color: #e2e2e2;
    border-radius: 4px;
    margin: 17px auto 0;
    display: block;

    @media (width >= ${mediaSizes.md}) {
        display: none;
    }
`

const ContentWrapper = styled.div`
    /* Phones: inside the overlay, so it moves with the visible area. */
    position: absolute;
    max-width: 100%;
    min-width: ${({minWidth}) => (minWidth ? minWidth + "px" : "auto")};
    height: ${({contentWrapperHeight, mobileFull}) => (mobileFull ? "calc(100% - 12px)" : contentWrapperHeight)};
    min-height: ${({minHeight}) => (minHeight || minHeight === 0 ? minHeight + "px" : "300px")};
    background: ${({ bgColor }) => bgColor || "white"};
    max-height: ${({mobileFull}) => (mobileFull ? "calc(100% - 12px)" : "90%")};
    overflow: auto;
    overscroll-behavior: contain;
    -webkit-overflow-scrolling: touch;
    width: 100%;
    border-radius: ${({ borderRadius, fromTop }) => {
        if (borderRadius) return fromTop ? `0 0 ${borderRadius} ${borderRadius}` : `${borderRadius} ${borderRadius} 0 0`;
        return fromTop ? "0 0 1rem 1rem" : "1rem 1rem 0 0";
    }};
    top: ${({ fromTop }) => fromTop ? "0" : "auto"};
    bottom: ${({ fromTop }) => fromTop ? "auto" : "0"};
    left: 0;

    @media (width >= ${mediaSizes.md}) {
        position: fixed;
        height: ${({contentWrapperHeight}) => contentWrapperHeight};
        max-height: 90dvh;
        bottom: auto;
        top: 50%;
        left: 50%;
        width: ${({widthPercentage}) => (widthPercentage ? `${widthPercentage}%` : "90%")};
        transform: translate(-50%, -50%);
        border-radius: ${({ borderRadius }) => borderRadius || "1rem"};
        max-width: ${({maxWidth}) => (maxWidth ? maxWidth + "px" : "700px")};
    }
    ${hideScrollBar};
`

const ContentDiv = styled.div`
    ${({setContentHeightFull}) => (setContentHeightFull ? "height: 100%;" : "")}
    position: relative;
    min-height: 100%;
    width: 100%;
    padding-block: 1.5rem calc(1.5rem + env(safe-area-inset-bottom, 0px));
    padding-inline: 1rem;
    @media (width >= ${mediaSizes.md}) {
        padding-block: 2rem;
        ${({isNoPaddingX}) => (isNoPaddingX ? "padding-inline: 0rem;" : "padding-inline: 1rem;")}
    }
`

const ContentCloseBtn = styled.button`
    all: unset;
    position: absolute;
    top: -0.7rem;
    right: 0.6rem;
    width: 32px;
    height: 32px;
    border-radius: 99px;
    color: ${({ closeBtnColor }) => closeBtnColor || defaultThemeColors.blue};
    font-weight: 500;
    display: flex;
    justify-content: center;
    align-items: center;
    transition: all 200ms ease;
    cursor: pointer;

    @media (width >= ${mediaSizes.md}) {
        top: 0.75rem;
        right: 0.75rem;
    }

    &:hover {
        color: ${defaultThemeColors.white};
        background-color: ${({ closeBtnColor }) => closeBtnColor || defaultThemeColors.blue};
    }
`
